import { SUBSTATUS } from "Services/Status/Substatus.types";
import { NOTIFICATIONTYPE } from "Store/Notification/ActionTypes";
import { ACTION_TYPE, getActionTypes } from "Store/Shared/Actions/Helpers";
import { API_URL } from "Store/Shared/Middleware/API/Constants";
import ACTIONS, { CANDIDATE, FETCH_ALL_CANDIDATES, FETCH_CANDIDATE, FETCH_CANDIDATE_DATASET, SAVE_CANDIDATE, SAVE_CANDIDATE_STATUS } from "./ActionTypes";
import { CandidateId } from "./PropTypes";

export const fetchCandidateOverviewByPagenumber = (
    overview_fields: string[],
    pageNumber: number = 1,
    filters: any = false,
    forceFetch: boolean = false
): ACTION_TYPE => {
    let flattenFilters = '';
    const filterKeys = Object.keys(filters);
    if (filterKeys) {
        for (const filterKey of filterKeys) {
            let values = filters[filterKey];
            if (Array.isArray(filters[filterKey])) {
                values = values.join(',');
            }
            flattenFilters += `&filter[${filterKey}]=${values}`;
        }
    }

    return {
        types: getActionTypes(ACTIONS.FETCH_CANDIDATE_OVERVIEW_PAGE),
        shouldCallAPI: ({ candidates }) => forceFetch || !candidates._isFetchingPage.includes(pageNumber),
        callAPI: {
            url: `/api/v2/overview?entity=candidate&page=${pageNumber}&overview_fields=${overview_fields ?? ''}${flattenFilters}`
        },
        payload: {
            page_number: pageNumber,
            isFiltering: flattenFilters.length > 0
        }
    }
}

export const createCandidate = (data: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.CREATE_CANDIDATE),
    callAPI: {
        method: 'POST',
        url: '/api/v2/candidates',
        body: data
    }
});

export const createCandidateValues = (id: CandidateId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.CREATE_CANDIDATE_VALUES),
    callAPI: {
        method: 'POST',
        url: `/api/v2/candidates/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    },
    callback: fetchCandidateV2(id)
})

export const backgroundCreateCandidateValues = (id: CandidateId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.BACKGROUND_CREATE_CANDIDATE_VALUES),
    callAPI: {
        method: 'POST',
        url: `/api/v2/candidates/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    },
})

export const fetchAllCandidatesV2 = (): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.FETCH_ALL_CANDIDATES),
    shouldCallAPI: state => state.candidates._forceFetch,
    callAPI: {
        url: '/api/v2/candidates'
    }
});

export const fetchCandidateV2 = (id: CandidateId): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.FETCH_CANDIDATE),
    callAPI: {
        url: `/api/v2/candidates/${id}`
    },
    callback: fetchCandidateValues(id)
});

export const fetchCandidateValues = (id: CandidateId): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.FETCH_CANDIDATE_VALUES),
    callAPI: {
        url: `/api/v2/candidates/${id}/values`
    }
});

export const fetchCandidateConcept = (): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.FETCH_CANDIDATE_CONCEPT),
    shouldCallAPI: state => state.candidates._forceFetch,
    callAPI: { url: `/api/v2/candidates?substatus=${SUBSTATUS.ACTIVE.RECORD_CONCEPT}` }
});

export const updateCandidateValues = (id: CandidateId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.SAVE_CANDIDATE_VALUES),
    callAPI: {
        method: 'PATCH',
        url: `/api/v2/candidates/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    },
    callback: fetchCandidateV2(id)
})

export const updateCandidate = (id: any, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.UPDATE_CANDIDATE),
    callAPI: {
        method: 'PATCH',
        url: `/api/v2/candidates/${id}`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    }
})

export const upsertCandidateValues = (id: any, data: any) => ({
    types: getActionTypes(ACTIONS.UPSERT_CANDIDATE_VALUES),
    callAPI: {
        method: 'PUT',
        url: `/api/v2/candidates/${id}/values`,
        body: {
            values: data
        }
    }
})

export const deleteCandidate = (id: CandidateId): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.DELETE_CANDIDATE),
    callAPI: {
        method: 'DELETE',
        url: `/api/v2/candidates/${id}`
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol verwijderd',
        [NOTIFICATIONTYPE.ERROR]: 'Verwijderen mislukt'
    },
})

export const saveCandidateValues = (id: CandidateId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.SAVE_CANDIDATE_VALUES),
    callAPI: {
        method: 'PATCH',
        url: `/api/v2/candidates/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    }
})

export const backgroundUpdateCandidateValues = (id: CandidateId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.BACKGROUND_UPDATE_CANDIDATE_VALUES),
    callAPI: {
        method: 'PATCH',
        url: `/api/v2/candidates/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    }
})

export function fetchAllCandidates(page, searchFilter, overviewFilter) {
    let urlParams = '';
    if (searchFilter) {
        urlParams += `&searchFilter=${searchFilter}`
    }

    Object.keys(overviewFilter)?.map((i) => {
        urlParams += `&${encodeURIComponent(i)}=${overviewFilter[i]}`;
    });
    
    return {
        types: [
            FETCH_ALL_CANDIDATES.REQUEST,
            FETCH_ALL_CANDIDATES.SUCCESS,
            FETCH_ALL_CANDIDATES.FAILURE
        ],
        // shouldCallAPI: (state: any) => Object.keys(state.candidates.saved).length <= 0,
        shouldCallAPI: (state: any) => true,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/overview?entity=candidate&page=${page}${urlParams}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
    }
}

export function fetchCandidate(id: string) {
    return {
        types: [
            FETCH_CANDIDATE.REQUEST,
            FETCH_CANDIDATE.SUCCESS,
            FETCH_CANDIDATE.FAILURE
        ],
        // shouldCallAPI: (state: any) => !state.candidates.saved[id] || state.candidates.saved[id].entity.length <= 0,
        shouldCallAPI: (state: any) => true,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/candidates/${id}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
    }
}

export function saveCandidate(candidateId: any, data: any) {
    return {
        types: [
            SAVE_CANDIDATE.REQUEST,
            SAVE_CANDIDATE.SUCCESS,
            SAVE_CANDIDATE.FAILURE
        ],
        notification: {
            [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol opgeslagen',
            [NOTIFICATIONTYPE.ERROR]: 'Opslaan mislukt'
        },
        shouldCallAPI: () => true,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/candidates/${candidateId}`,
            {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify(data)
            }),
        callback: fetchCandidateValues(candidateId)
    }
}

export function saveCandidateEntity(candidateId: any, data: any) {
    return {
        types: [
            SAVE_CANDIDATE_STATUS.REQUEST,
            SAVE_CANDIDATE_STATUS.SUCCESS,
            SAVE_CANDIDATE_STATUS.FAILURE
        ],
        notification: {
            [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol opgeslagen',
            [NOTIFICATIONTYPE.ERROR]: 'Opslaan mislukt'
        },
        shouldCallAPI: () => true,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/candidates/${candidateId}`,
            {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify(data)
            })
    }
}

export function editCandidate(candidateId: any, fieldId: any, valueId: any, value: any) {
    return {
        type: CANDIDATE.EDIT,
        payload: {
            candidateId: candidateId,
            fieldId: fieldId,
            valueId: valueId,
            value: value
        }
    }
}

export function resetCandidate(candidateId: any, fieldIds: any) {
    return {
        type: CANDIDATE.RESET,
        payload: {
            candidateId: candidateId,
            fieldIds: fieldIds
        }
    }
}

export function fetchCandidateDataset() {
    return {
        types: [
            FETCH_CANDIDATE_DATASET.REQUEST,
            FETCH_CANDIDATE_DATASET.SUCCESS,
            FETCH_CANDIDATE_DATASET.FAILURE
        ],
        shouldCallAPI: (state: any) => state.candidates.datasets._forceFetch,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/candidates/dataset`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
    }
}